import {
  compactFormat,
  dataLabelsCompact,
  dataLabelsDecimals,
  dataLabelsShowPercent,
  dataLabelsSizes,
  dataLabelsTopEntries,
  dimension,
  dimensionSelector,
  legendPosition,
  palette,
  primaryMetric,
  primaryMetricSelector,
  showDataLabels,
  showItems,
} from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { Config } from "@/types/charts";

export default <Config>{
  family: [FAMILY.DECOMPOSITION],
  title: "Nested Doughnut Chart",
  defaultHeight: 6,
  dataModes: ["project", "explore", "dual"],
  controls: [dimension, primaryMetric],

  settings: {
    palette,
    showItems: { ...showItems, gridPosition: [2, 0] },
    legendPosition: { ...legendPosition, gridPosition: [3, 0] },
    compactFormat: { ...compactFormat, gridPosition: [4, 0] },
    decompositionMetric: {
      default: "channel",
      type: "select",
      label: "Decomposition Metric",
      dictionary: "dimensionMetrics",
      apiParameter: "dimension",
      gridPosition: [0, 0],
      tooltipOrder: 0,
    },
    displayMetric: {
      default: "spend",
      type: "select",
      label: "Display Metric 1",
      dictionary: "displayMetrics",
      gridPosition: [1, 0],
      tooltipOrder: 1,
    },
    displayMetricSecondary: {
      default: "impressions",
      type: "select",
      label: "Display Metric 2",
      dictionary: "displayMetrics",
      gridPosition: [1, 1],
      tooltipOrder: 1,
    },
    holeSize: {
      default: 50,
      type: "select",
      label: "Doughnut hole",
      dictionary: "percents",
      gridPosition: [0, 1],
    },
    dimensionSelector: { ...dimensionSelector, gridPosition: [5, 0] },
    primaryMetricSelector: { ...primaryMetricSelector, gridPosition: [6, 0] },
  },
  planSettings: {
    palette,
    showItems: { ...showItems, gridPosition: [2, 0] },
    legendPosition: { ...legendPosition, gridPosition: [3, 0] },
    compactFormat: { ...compactFormat, gridPosition: [4, 0] },
    decompositionMetric: {
      default: "channel",
      type: "select",
      label: "Decomposition Metric",
      dictionary: "dimensionMetrics",
      apiParameter: "dimension",
      gridPosition: [0, 0],
      tooltipOrder: 0,
    },
    displayMetric: {
      default: "channel_budget",
      type: "select",
      label: "Display Metric 1",
      dictionary: "displayMetrics",
      gridPosition: [1, 0],
      tooltipOrder: 1,
    },
    displayMetricSecondary: {
      default: "impressions",
      type: "select",
      label: "Display Metric 2",
      dictionary: "displayMetrics",
      gridPosition: [1, 1],
      tooltipOrder: 1,
    },
    holeSize: {
      default: 50,
      type: "select",
      label: "Doughnut hole",
      dictionary: "percents",
      gridPosition: [0, 1],
    },
    dimensionSelector: { ...dimensionSelector, gridPosition: [5, 0] },
    primaryMetricSelector: { ...primaryMetricSelector, gridPosition: [6, 0] },
    showDataLabels: { ...showDataLabels, gridPosition: [7, 0] },
    dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [7, 1] },
    dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [8, 0] },
    dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [8, 1] },
    dataLabelsShowPercent: { ...dataLabelsShowPercent, gridPosition: [9, 0] },
    dataLabelsTopEntries: { ...dataLabelsTopEntries, gridPosition: [9, 1] },
  },
  planExploreSettings: {
    palette,
    showItems: { ...showItems, gridPosition: [2, 0] },
    legendPosition: { ...legendPosition, gridPosition: [3, 0] },
    compactFormat: { ...compactFormat, gridPosition: [4, 0] },
    decompositionMetric: {
      default: "channel",
      type: "select",
      label: "Decomposition Metric",
      dictionary: "dimensionMetrics",
      apiParameter: "dimension",
      gridPosition: [0, 0],
      tooltipOrder: 0,
    },
    displayMetric: {
      default: "spend",
      type: "select",
      label: "Display Metric 1",
      dictionary: "displayMetrics",
      gridPosition: [1, 0],
      tooltipOrder: 1,
    },
    displayMetricSecondary: {
      default: "impressions",
      type: "select",
      label: "Display Metric 2",
      dictionary: "displayMetrics",
      gridPosition: [1, 1],
      tooltipOrder: 1,
    },
    holeSize: {
      default: 50,
      type: "select",
      label: "Doughnut hole",
      dictionary: "percents",
      gridPosition: [0, 1],
    },
    dimensionSelector: { ...dimensionSelector, gridPosition: [5, 0] },
    primaryMetricSelector: { ...primaryMetricSelector, gridPosition: [6, 0] },
    showDataLabels: { ...showDataLabels, gridPosition: [7, 0] },
    dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [7, 1] },
    dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [8, 0] },
    dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [8, 1] },
    dataLabelsShowPercent: { ...dataLabelsShowPercent, gridPosition: [9, 0] },
    dataLabelsTopEntries: { ...dataLabelsTopEntries, gridPosition: [9, 1] },
  },
  planDualSettings: {
    palette,
    displayMetricSource: {
      default: "plan",
      dictionary: "metricSources",
    },
    displayMetricSecondarySource: {
      default: "explore",
      dictionary: "metricSources",
    },
    decompositionMetric: {
      default: "channel",
      type: "select",
      label: "Grouping Dimension (Project + Explore)",
      dictionary: "dualDimensionMetrics",
      apiParameter: "dimension",
      gridPosition: [0, 0],
      tooltipOrder: 0,
    },
    holeSize: {
      default: 50,
      type: "select",
      label: "Doughnut hole",
      dictionary: "percents",
      gridPosition: [0, 1],
    },
    displayMetric: {
      default: "channel_budget",
      type: "select",
      label: "Inner Ring Metric",
      dictionary: "displayMetrics",
      sourceFrom: "displayMetricSource",
      gridPosition: [2, 0],
      tooltipOrder: 1,
    },
    displayMetricSecondary: {
      default: "impressions",
      type: "select",
      label: "Outer Ring Metric",
      dictionary: "displayMetrics",
      sourceFrom: "displayMetricSecondarySource",
      gridPosition: [3, 0],
      tooltipOrder: 1,
    },
    legendPosition: { ...legendPosition, gridPosition: [4, 0] },
    compactFormat: { ...compactFormat, gridPosition: [5, 0] },
    showDataLabels: { ...showDataLabels, gridPosition: [6, 0] },
    dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [6, 1] },
    dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [7, 0] },
    dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [7, 1] },
    dataLabelsShowPercent: { ...dataLabelsShowPercent, gridPosition: [8, 0] },
    dataLabelsTopEntries: { ...dataLabelsTopEntries, gridPosition: [8, 1] },
  },
};
